<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()

const props = defineProps({
  delivery: { type: Object, required: true },
  positions_url: { type: String, required: true }
})

const positions  = ref([])
const lastFixAt  = ref(null)
const status     = ref(props.delivery.status)
const ageSeconds = ref(null)

const mapEl      = ref(null)
let   map        = null
let   driverMarker = null
let   destMarker = null
let   trail      = null
let   pollHandle = null
let   ageHandle  = null

const isLive = computed(() => status.value === 'in_progress' || status.value === 'assigned' || status.value === 'pending')
const hasFix = computed(() => positions.value.length > 0)
const lastPos = computed(() => positions.value[positions.value.length - 1])

function eta(iso) {
  if (!iso) return null
  const d = new Date(iso)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

async function fetchPositions() {
  try {
    const r = await fetch(props.positions_url, { headers: { Accept: 'application/json' } })
    if (!r.ok) {
      if (r.status === 404) { status.value = 'completed' }
      return
    }
    const data = await r.json()
    status.value    = data.status || status.value
    positions.value = data.positions || []
    if (positions.value.length) {
      lastFixAt.value = new Date(positions.value[positions.value.length - 1].t).getTime()
    }
    updateMap()
  } catch (e) {
    // Silent — the next poll will retry; we don't surface network blips
    // to the customer. The "last fix N ago" age indicator is the signal.
  }
}

function updateMap() {
  if (!map || !window.L) return
  const L = window.L
  if (!hasFix.value) return

  const latlngs = positions.value.map(p => [p.lat, p.lng])
  const last    = latlngs[latlngs.length - 1]

  if (!driverMarker) {
    const icon = L.divIcon({
      className: '',
      html: `<div style="width:18px;height:18px;border-radius:50%;background:var(--accent);border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    })
    driverMarker = L.marker(last, { icon }).addTo(map)
  } else {
    driverMarker.setLatLng(last)
  }

  if (latlngs.length > 1) {
    if (trail) { map.removeLayer(trail) }
    const accent = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#2563eb'
    trail = L.polyline(latlngs, { color: accent, weight: 3, opacity: 0.7 }).addTo(map)
  }

  // First fix or significant move → recenter to include both markers
  if (destMarker && driverMarker) {
    const group = L.featureGroup([driverMarker, destMarker])
    map.fitBounds(group.getBounds(), { padding: [40, 40], maxZoom: 16 })
  } else {
    map.setView(last, 15)
  }
}

function tickAge() {
  if (!lastFixAt.value) return
  ageSeconds.value = Math.max(0, Math.round((Date.now() - lastFixAt.value) / 1000))
}

onMounted(async () => {
  const L = (await import('leaflet')).default
  await import('leaflet/dist/leaflet.css')
  window.L = L

  const initialCenter = (props.delivery.lat && props.delivery.lng)
    ? [props.delivery.lat, props.delivery.lng]
    : [7.8959, 98.2964]  // Phuket fallback

  map = L.map(mapEl.value, { zoomControl: true, attributionControl: true }).setView(initialCenter, 14)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap',
    maxZoom: 19,
  }).addTo(map)

  if (props.delivery.lat && props.delivery.lng) {
    const dest = L.divIcon({
      className: '',
      html: `<div style="width:14px;height:14px;border-radius:50%;background:var(--ok);border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.35)"></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7]
    })
    destMarker = L.marker([props.delivery.lat, props.delivery.lng], { icon: dest })
      .bindTooltip(props.delivery.address || t('public.tracking.destination'))
      .addTo(map)
  }

  await fetchPositions()

  // Poll every 8s while delivery is live. Stop when terminal.
  pollHandle = setInterval(() => {
    if (!isLive.value) {
      clearInterval(pollHandle)
      pollHandle = null
      return
    }
    fetchPositions()
  }, 8000)

  ageHandle = setInterval(tickAge, 1000)
})

onBeforeUnmount(() => {
  if (pollHandle) clearInterval(pollHandle)
  if (ageHandle)  clearInterval(ageHandle)
  if (map) { map.remove(); map = null }
})

const ageLabel = computed(() => {
  if (ageSeconds.value == null) return null
  const s = ageSeconds.value
  if (s < 60) return t('public.tracking.age_seconds', { n: s })
  return t('public.tracking.age_minutes', { n: Math.floor(s / 60) })
})
</script>

<template>
  <Head><title>{{ t('public.tracking.head_title') }}</title></Head>
  <section class="track">
    <header class="track__header">
      <h1 class="track__title">
        {{ delivery.kind === 'handover' ? t('public.tracking.title_handover') : t('public.tracking.title_pickup') }}
      </h1>
      <p class="track__driver">
        <template v-if="delivery.driver?.first_name">
          {{ t('public.tracking.with_driver', { name: delivery.driver.first_name }) }}
        </template>
        <template v-else>{{ t('public.tracking.with_driver_fallback') }}</template>
      </p>
      <p v-if="delivery.address" class="track__addr">
        <strong>{{ t('public.tracking.meeting_at') }}:</strong> {{ delivery.address }}
      </p>
      <p v-if="eta(delivery.scheduled_for)" class="track__eta">
        <strong>{{ t('public.tracking.eta') }}:</strong> {{ eta(delivery.scheduled_for) }}
      </p>
    </header>

    <div ref="mapEl" class="track__map" />

    <footer class="track__footer">
      <div v-if="!isLive" class="track__status track__status--done">
        {{ status === 'completed' ? t('public.tracking.completed') : t('public.tracking.no_longer_active') }}
      </div>
      <div v-else-if="!hasFix" class="track__status">{{ t('public.tracking.waiting_for_fix') }}</div>
      <div v-else class="track__status">
        <span class="track__pulse" aria-hidden="true" />
        {{ t('public.tracking.driver_en_route') }}
        <span v-if="ageLabel" class="track__age">· {{ ageLabel }}</span>
      </div>
      <a v-if="delivery.driver?.phone" :href="`tel:${delivery.driver.phone}`" class="track__call">
        {{ t('public.tracking.call_driver') }}
      </a>
    </footer>
  </section>
</template>

<style scoped>
.track {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  padding: 1.25rem 1rem 1.5rem;
  gap: 0.875rem;
}
.track__header { display: grid; gap: 0.25rem; }
.track__title {
  font-family: var(--font-display, var(--display));
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.track__driver { margin: 0; color: var(--inkSoft); font-size: 0.95rem; }
.track__addr, .track__eta { margin: 0; font-size: 0.9rem; color: var(--inkSoft); }
.track__addr strong, .track__eta strong { color: var(--ink); font-weight: 600; }
.track__map {
  width: 100%;
  height: 22rem;
  border-radius: 0.875rem;
  overflow: hidden;
  border: 1px solid var(--line);
  background: var(--surfaceAlt);
}
@media (min-width: 640px) { .track__map { height: 28rem; } }
.track__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.track__status {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--inkSoft);
  font-size: 0.875rem;
}
.track__status--done { color: var(--muted); }
.track__pulse {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: var(--ok);
  box-shadow: 0 0 0 0 color-mix(in oklch, var(--ok) 60%, transparent);
  animation: track-pulse 1.6s ease-out infinite;
}
@keyframes track-pulse {
  0%   { box-shadow: 0 0 0 0 color-mix(in oklch, var(--ok) 60%, transparent); }
  70%  { box-shadow: 0 0 0 0.65rem color-mix(in oklch, var(--ok) 0%, transparent); }
  100% { box-shadow: 0 0 0 0 color-mix(in oklch, var(--ok) 0%, transparent); }
}
.track__age { color: var(--muted); }
.track__call {
  display: inline-block;
  padding: 0.55rem 1.1rem;
  background: var(--accent);
  color: var(--accentInk);
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}
.track__call:hover { opacity: 0.9; }
</style>
